import { Inspection } from "../types/inspection";
import { RegimeMapping, RegimeMappings } from "../types/regime";
import { isDefaultDefectCode } from "./defectCodeHelper";

/**
 * LaunchDarkly key (camelCased by the SDK) for `release-authority-mappings`,
 * the flag gating the regime-code column.
 */
export const REGIME_MAPPINGS_RELEASE_FLAG = "releaseAuthorityMappings";

/**
 * ASIMS short name for the ID Condition inspection. The regime mappings only
 * cover this type today (Pre-PSC follows later). Matched on the short name
 * rather than `inspectionTypeId`, which differs between environments — the
 * same type is 27 on dev/staging and 24 on prod.
 */
export const ID_CONDITION_INSPECTION_SHORT_NAME = "IDCI";

export const isIdConditionInspection = (
    inspection: Inspection | undefined
): boolean =>
    inspection?.inspectionTypeShortName ===
    ID_CONDITION_INSPECTION_SHORT_NAME;

/** Rank a mapping sorts on. The file is hand-prepared upstream, so a missing
 *  or non-numeric rank sorts last rather than poisoning the whole comparison
 *  with NaN and leaving the cell in arbitrary order. */
const rankOf = ({ order }: RegimeMapping) =>
    Number.isFinite(order) ? order : Number.POSITIVE_INFINITY;

/**
 * The regime codes mapped to an Idwal defect code, as a display string —
 * `PMoU:01306, MoU:01305, TMoU:01304`.
 *
 * Ordered by the workbook's presentation rank, then by code so a shared rank
 * still renders the same way every time. An unassigned or placeholder defect
 * code maps to nothing, as does a code the mappings don't cover (not every
 * Idwal code has a regime equivalent) — both render as an empty cell.
 */
export const formatRegimeCodes = (
    typeCode: string | null | undefined,
    mappings: RegimeMappings
): string => {
    if (isDefaultDefectCode(typeCode)) {
        return "";
    }

    const mapped = mappings[typeCode as string];

    if (!mapped?.length) {
        return "";
    }

    return [...mapped]
        .sort((a, b) => rankOf(a) - rankOf(b) || a.code.localeCompare(b.code))
        .map(({ regime, code }) => `${regime}:${code}`)
        .join(", ");
};
